import type { KeyPress } from "../../api/client";

/**
 * How a call's key presses read in the Results tab.
 *
 * Pure, so the panel stays a renderer (the repository keeps logic in .ts
 * modules with .test.ts files and no component tests).
 *
 * The question this answers is "what did the caller choose, and to what" --
 * which the system could not answer at all before migration 0028. The
 * worker logged the gather's chosen PORT, and a port names the target node
 * ("mark"), not the digit, so a completed menu call showed no sign that
 * anybody had pressed anything.
 */

/** One row in the expanded key-press list. */
export interface KeyPressRow {
  /** What the caller did: "Pressed 1", "No key pressed", "Invalid key". */
  action: string;
  /** The question it answered, as authored. */
  prompt: string;
  /** "" when the prompt was heard once, else "after 3 plays". */
  attempts: string;
  /** The flow node, for an author matching this back to the canvas. */
  nodeId: string;
}

function action(press: KeyPress): string {
  switch (press.outcome) {
    case "key":
      // An empty key with a "key" outcome cannot reach here from the API
      // (a database constraint refuses the row), but a hand-made or future
      // payload should degrade to something readable rather than "Pressed ".
      return press.key === "" ? "Pressed a key" : `Pressed ${press.key}`;
    case "timeout":
      return "No key pressed";
    case "invalid":
      return "Invalid key";
    default:
      return press.outcome;
  }
}

/**
 * The prompt is stored as authored, so it still contains `{{first_name}}`.
 * Left exactly as written rather than blanked or filled with a placeholder:
 * an author recognises their own prompt text, and substituting anything
 * would either be a lie about what the caller heard or a second copy of the
 * call's variable values.
 */
export function keyPressRows(presses: KeyPress[] | undefined): KeyPressRow[] {
  if (!Array.isArray(presses)) return [];
  return presses.map((press) => ({
    action: action(press),
    prompt: press.prompt,
    attempts: press.attempts > 1 ? `after ${press.attempts} plays` : "",
    nodeId: press.node_id,
  }));
}

/**
 * The one-line version for a collapsed row.
 *
 * Only actual digits, joined: a reader scanning the list wants to see "1"
 * or "1, 4", and timeouts are noise at that density. Returns "" when there
 * is nothing to say, so the caller renders nothing rather than a dash.
 */
export function keyPressSummary(presses: KeyPress[] | undefined): string {
  if (!Array.isArray(presses)) return "";
  const keys = presses.filter((p) => p.outcome === "key" && p.key !== "").map((p) => p.key);
  return keys.join(", ");
}
